import { useAuthStatus } from 'lib/auth/AuthStatusContext';
import { AUTH_STATUS } from 'lib/auth/constants';
import { useRouter } from 'next/router';
import {
  createContext,
  useCallback,
  useContext,
  useMemo,
} from 'react';
import { redeemPerkReward, unlockPerk } from './api';
import { PERK_TYPES } from './constants';
import { Perk } from './types';
import { usePerks } from './usePerks';
import { useUnlockablePerks } from './useUnlockablePerks';

interface YBPerksContextProps {
  isLoading?: boolean;
  redeemablePerks: Perk[];
  nextUnlockablePerk: Perk | null;
  totalUnlockablePerks: number;
  totalUnlockedPerks: number;
  onUnlock: (perkId: number) => Promise<Perk>;
  onRedeem: (perkRewardId: number, code: string) => Promise<void>;
}

export const ybPerksContextDefaultValues: YBPerksContextProps = {
  isLoading: false,
  redeemablePerks: [],
  nextUnlockablePerk: null,
  totalUnlockablePerks: 0,
  totalUnlockedPerks: 0,
  onUnlock: async (_: number) => {
    return {} as Perk;
  },
  onRedeem: async (_i: number, _c: string) => {},
};

export const YBPerksContext = createContext<YBPerksContextProps>(
  ybPerksContextDefaultValues,
);

export const YBPerksContextProvider = ({
  children,
}: {
  children: React.ReactNode;
}) => {
  const router = useRouter();

  const {
    totalUnlockable: totalUnlockablePerks,
    nextUnlockable: nextUnlockablePerk,
    totalUnlocked: totalUnlockedPerks,
    isLoading,
    mutate: mutateUnlockablePerks,
  } = useUnlockablePerks();

  const { authStatus } = useAuthStatus();

  const {
    perks: redeemablePerks,
    mutate: mutateRedeemablePerks,
  } = usePerks({
    params: {
      state: 'redeemable',
      type: `${PERK_TYPES.INTERNAL_GENERIC},${PERK_TYPES.INTERNAL_UNIQUE}`,
      page: 1,
      perPage: 99,
      status: 'available',
    },
    isAllowToFetch:
      router.pathname === '/checkout' &&
      authStatus === AUTH_STATUS.AUTHENTICATED,
  });

  const onUnlock = useCallback(
    async (perkId: number) => {
      try {
        const { data: updatedPerk } = await unlockPerk(perkId);
        await mutateRedeemablePerks();
        mutateUnlockablePerks();
        return updatedPerk;
      } catch (error) {
        throw error;
      }
    },
    [mutateRedeemablePerks, mutateUnlockablePerks],
  );

  const onRedeem = useCallback(
    async (perkRewardId: number, code: string) => {
      try {
        await redeemPerkReward(perkRewardId, code);
        await mutateRedeemablePerks();
        mutateUnlockablePerks();
      } catch (error) {
        throw error;
      }
    },
    [mutateRedeemablePerks, mutateUnlockablePerks],
  );

  const contextValue = useMemo(
    () => ({
      isLoading,
      redeemablePerks,
      totalUnlockablePerks,
      totalUnlockedPerks,
      nextUnlockablePerk,
      onUnlock,
      onRedeem,
    }),
    [
      isLoading,
      nextUnlockablePerk,
      onRedeem,
      onUnlock,
      redeemablePerks,
      totalUnlockedPerks,
      totalUnlockablePerks,
    ],
  );

  return (
    <YBPerksContext.Provider value={contextValue}>
      {children}
    </YBPerksContext.Provider>
  );
};

export const useYBPerksContext = () => useContext(YBPerksContext);
